import {
  CalculationMode,
  ProblemSeverity,
  ValidationProblemCode,
  ValidationProblemResponse,
} from '@ydistri/api-sdk';
import {
  fieldErrors,
  mergeProblems,
  messageFor,
  problemKey,
} from '../../Configuration/Validator/problemsLib';
import { ManualCalculationData } from '../../Calculations/common/ManualCalculationSelect';
import { MergedCalculationData } from './TemplateMultiselectAndSort/MergedCalculationTemplateSelect';

/**
 * The merged request's subordinate rows in the order the request sends them - manual calculations
 * first, then the templates in their sorted order. A server problem on `subordinateCalculations[i]`
 * is about the i-th of these.
 */
export interface RequestRow {
  kind: 'manual' | 'template';
  id: number;
  title: string;
}

export const requestRows = (
  manual: ManualCalculationData | undefined,
  merged: MergedCalculationData | undefined,
): RequestRow[] => [
  ...(manual?.manualCalculationIds ?? []).map(id => ({
    kind: 'manual' as const,
    id,
    title: manual?.calculationTitles[id] ?? '',
  })),
  ...(merged?.sortedTemplateIds ?? []).map(id => ({
    kind: 'template' as const,
    id,
    title: merged?.calculationTitles[id] ?? '',
  })),
];

export const SUBORDINATES_PATH = 'subordinateCalculations';
const subordinateTitlePath = /^subordinateCalculations\[(\d+)\]\.title$/;

export const subordinatePath = (index: number): string => `${SUBORDINATES_PATH}[${index}].title`;

/** The row index a server path names, or undefined for anything else. */
export const rowIndexOfPath = (path: string | null | undefined): number | undefined => {
  const match = subordinateTitlePath.exec(path ?? '');
  return match ? Number(match[1]) : undefined;
};

export interface KeyedProblem extends ValidationProblemResponse {
  key: string;
}

export const keyed = (problem: ValidationProblemResponse): KeyedProblem => ({
  ...problem,
  key: problemKey(problem),
});

/**
 * The local copies of the merged request's rules, as problems with the server's identity: the same
 * code on the same path the server would name, so a server answer replaces the copy instead of
 * standing next to it.
 */
export const localSelectionProblems = (
  mode: CalculationMode,
  rows: RequestRow[],
): KeyedProblem[] => {
  if (mode !== CalculationMode.Merged) return [];
  const problems: KeyedProblem[] = [];
  if (rows.length === 0) {
    problems.push(
      keyed({
        code: ValidationProblemCode.CalculationNoSubordinates,
        severity: ProblemSeverity.Error,
        message: messageFor(ValidationProblemCode.CalculationNoSubordinates),
        path: SUBORDINATES_PATH,
      }),
    );
  }
  rows.forEach((row, index) => {
    if (row.title.trim() === '') {
      problems.push(
        keyed({
          code: ValidationProblemCode.CalculationTitleRequired,
          severity: ProblemSeverity.Error,
          message: messageFor(ValidationProblemCode.CalculationTitleRequired),
          path: subordinatePath(index),
        }),
      );
    }
  });
  return problems;
};

/** Server first; a local copy the server also answered is dropped. */
export const reconcile = (
  local: KeyedProblem[],
  server: ValidationProblemResponse[],
): KeyedProblem[] => mergeProblems(local, server.map(keyed));

/** "template #5770" or "manual calculation #2874" for the row a problem's path names. */
export const rowLabel = (
  rows: RequestRow[],
  path: string | null | undefined,
): string | undefined => {
  const index = rowIndexOfPath(path);
  const row = index === undefined ? undefined : rows[index];
  if (!row) return undefined;
  return row.kind === 'template' ? `template #${row.id}` : `manual calculation #${row.id}`;
};

/** The problems that name a template row, by template id, for the row to show under its title. */
export const templateRowErrors = (
  problems: ValidationProblemResponse[],
  rows: RequestRow[],
): Record<number, string[]> => {
  const errors: Record<number, string[]> = {};
  problems.forEach(p => {
    const index = rowIndexOfPath(p.path);
    const row = index === undefined ? undefined : rows[index];
    if (row?.kind === 'template') errors[row.id] = [...(errors[row.id] ?? []), p.message];
  });
  return errors;
};

/**
 * A server problem about one row, remembered by the row it was about - kind, id, the title that was
 * refused and the row's revision at the time - not by its index: the user may reorder, edit or
 * remove rows while the answer is in flight, and a row's problem must follow the row and no further.
 */
export interface RowProblem {
  row: RequestRow;
  revision: number;
  problem: ValidationProblemResponse;
}

/** How many times each row (by kind and id) had its title changed or was removed; absent is 0. */
export type RowRevisions = Record<string, number>;

export const rowKey = (row: Pick<RequestRow, 'kind' | 'id'>): string => `${row.kind}:${row.id}`;

/**
 * The revisions after the rows changed from `before` to `after`: a row whose title changed or which
 * is gone advances; a reorder, another row's edit or a badge answering advances nothing. A row
 * removed and added again carries the advanced revision, so a refusal of its earlier self no longer
 * applies.
 */
export const nextRevisions = (
  previous: RowRevisions,
  before: RequestRow[],
  after: RequestRow[],
): RowRevisions => {
  const next = { ...previous };
  before.forEach(row => {
    const now = after.find(r => r.kind === row.kind && r.id === row.id);
    if (!now || now.title !== row.title) next[rowKey(row)] = (previous[rowKey(row)] ?? 0) + 1;
  });
  return next;
};

export interface SortedRefusal<N extends string> {
  /** Problems on the form's own fields, ready for form.setFields. */
  fields: { name: N; errors: string[] }[];
  /** Problems on a subordinate row the request had, attached to that row. */
  rows: RowProblem[];
  /** Everything else - the root, an unknown path, a row the request did not have. */
  other: string[];
}

/**
 * Sorts a refused request's problems: onto the form's fields, onto the subordinate rows, or into
 * the toast. Nothing is dropped: a problem on a row the request did not send is `other`.
 */
export const sortRefusal = <N extends string>(
  problems: ValidationProblemResponse[],
  rows: RequestRow[],
  fieldOf: Record<string, N>,
  revisions: RowRevisions = {},
): SortedRefusal<N> => {
  const onRows: RowProblem[] = [];
  const rest: ValidationProblemResponse[] = [];
  problems.forEach(p => {
    const index = rowIndexOfPath(p.path);
    const row = index === undefined ? undefined : rows[index];
    if (row) {
      onRows.push({ row, revision: revisions[rowKey(row)] ?? 0, problem: p });
    } else {
      rest.push(p);
    }
  });
  const { fields, other } = fieldErrors(rest, fieldOf);
  return { fields, rows: onRows, other };
};

/**
 * The remembered row problems still about a row the request has now - same kind, id and title, and
 * with revisions given, the same revision: a row edited and edited back, or removed and added again,
 * is not the row that was refused.
 */
export const stillRefused = (
  remembered: RowProblem[],
  rows: RequestRow[],
  revisions?: RowRevisions,
): RowProblem[] =>
  remembered.filter(
    ({ row, revision }) =>
      rows.some(r => r.kind === row.kind && r.id === row.id && r.title === row.title) &&
      (revisions === undefined || (revisions[rowKey(row)] ?? 0) === revision),
  );

/**
 * The remembered row problems placed on the rows as they are now: a problem follows its row through
 * a reorder; one about a row that is gone or whose title changed is left out. The modal also drops
 * those from what it remembers, so editing the title back does not bring the problem back.
 */
export const currentRowProblems = (
  remembered: RowProblem[],
  rows: RequestRow[],
): ValidationProblemResponse[] =>
  stillRefused(remembered, rows).map(({ row, problem }) => ({
    ...problem,
    path: subordinatePath(rows.findIndex(r => r.kind === row.kind && r.id === row.id)),
  }));

/**
 * What the merged modal shows under its rows: the local copies of the rules, with the server's
 * answer over the copy of the same identity - one map keyed by (code, target, path).
 */
export const shownProblems = (
  mode: CalculationMode,
  rows: RequestRow[],
  remembered: RowProblem[],
): KeyedProblem[] =>
  reconcile(localSelectionProblems(mode, rows), currentRowProblems(remembered, rows));
